import { ApplicationInsights } from '@microsoft/applicationinsights-web';

export const TELEMETRY_EVENT_NAMES = [
  'app.loaded',
  'library.viewed',
  'game.started',
  'game.returned_to_library',
  'playable_icon.selected',
  'shooter.started',
  'shooter.level_started',
  'shooter.level_completed',
  'shooter.game_over',
  'shooter.won',
  'shooter.performance_warning',
  'ranger.mission_started',
  'ranger.mission_completed',
  'ranger.game_over',
  'ranger.class_selected',
  'neon_relay.route_started',
  'neon_relay.route_completed',
  'neon_relay.signal_lost',
  'neon_relay.won',
];

const ALLOWED_EVENTS = new Set(TELEMETRY_EVENT_NAMES);
const MAX_STRING_LENGTH = 180;
const MAX_ARRAY_LENGTH = 8;
const MAX_OBJECT_KEYS = 18;
const MAX_DEPTH = 3;
const REDACTED_VALUE = '[redacted]';
const SENSITIVE_KEY_PATTERN = /(authorization|cookie|email|key|password|secret|token|url)/i;

let appInsightsClient = null;
let appInsightsStarted = false;
let telemetryClientFactory = createApplicationInsightsClient;
const repeatedErrorCounts = new Map();

export function getBuildContext() {
  return {
    app: 'arcade-shooter',
    buildNumber: process.env.REACT_APP_BUILD_NUMBER || 'local',
    commitSha: process.env.REACT_APP_COMMIT_SHA || 'local',
    releaseChannel: process.env.NODE_ENV === 'production' ? 'production' : 'development',
    userAgentFamily: getUserAgentFamily(window.navigator?.userAgent ?? ''),
  };
}

export function getUserAgentFamily(userAgent = '') {
  if (/Edg\//.test(userAgent)) {
    return 'edge';
  }

  if (/Chrome\//.test(userAgent)) {
    return 'chrome';
  }

  if (/Safari\//.test(userAgent) && !/Chrome\//.test(userAgent)) {
    return 'safari';
  }

  if (/Firefox\//.test(userAgent)) {
    return 'firefox';
  }

  return userAgent ? 'other' : 'unknown';
}

export function sanitizeTelemetryProperties(properties = {}, depth = 0) {
  if (depth > MAX_DEPTH) {
    return '[truncated]';
  }

  if (properties === null || ['string', 'number', 'boolean'].includes(typeof properties)) {
    return sanitizePrimitive(properties);
  }

  if (Array.isArray(properties)) {
    return properties
      .slice(0, MAX_ARRAY_LENGTH)
      .map((item) => sanitizeTelemetryProperties(item, depth + 1));
  }

  if (typeof properties !== 'object') {
    return String(properties);
  }

  return Object.entries(properties)
    .slice(0, MAX_OBJECT_KEYS)
    .reduce((safeProperties, [key, value]) => {
      if (SENSITIVE_KEY_PATTERN.test(key)) {
        return {
          ...safeProperties,
          [key]: REDACTED_VALUE,
        };
      }

      return {
        ...safeProperties,
        [key]: sanitizeTelemetryProperties(value, depth + 1),
      };
    }, {});
}

function sanitizePrimitive(value) {
  if (typeof value !== 'string') {
    return value;
  }

  return value.length > MAX_STRING_LENGTH
    ? `${value.slice(0, MAX_STRING_LENGTH)}...`
    : value;
}

function shouldUseConsoleSink() {
  if (process.env.NODE_ENV === 'test' && process.env.REACT_APP_TEST_TELEMETRY_CONSOLE !== 'true') {
    return false;
  }

  return process.env.NODE_ENV !== 'production' || !getApplicationInsightsClient();
}

function getApplicationInsightsClient() {
  if (appInsightsStarted) {
    return appInsightsClient;
  }

  appInsightsStarted = true;
  const connectionString = process.env.REACT_APP_APPLICATIONINSIGHTS_CONNECTION_STRING;

  if (!connectionString) {
    return null;
  }

  try {
    appInsightsClient = telemetryClientFactory(connectionString);
  } catch (error) {
    appInsightsClient = null;
    console.error('[telemetry:init_failed]', error);
  }

  return appInsightsClient;
}

function createApplicationInsightsClient(connectionString) {
  const client = new ApplicationInsights({
    config: {
      connectionString,
      disableAjaxTracking: true,
      disableExceptionTracking: true,
      disableFetchTracking: true,
      disableTelemetry: false,
      enableAutoRouteTracking: false,
    },
  });

  client.loadAppInsights();
  client.addTelemetryInitializer?.((envelope) => {
    const baseData = envelope?.data?.baseData;

    if (!baseData) {
      return true;
    }

    baseData.properties = {
      ...getBuildContext(),
      ...(baseData.properties ?? {}),
    };
    return true;
  });

  return client;
}

export function trackEvent(name, properties = {}) {
  if (!ALLOWED_EVENTS.has(name)) {
    return false;
  }

  const safeProperties = {
    ...getBuildContext(),
    ...sanitizeTelemetryProperties(properties),
  };
  const client = getApplicationInsightsClient();

  if (shouldUseConsoleSink()) {
    console.info('[telemetry:event]', name, safeProperties);
  }

  if (client && typeof client.trackEvent === 'function') {
    client.trackEvent({ name }, safeProperties);
  }
  return true;
}

export function trackError(error, properties = {}) {
  const normalizedError = normalizeError(error);
  const signature = `${normalizedError.name}:${normalizedError.message}`;
  const count = (repeatedErrorCounts.get(signature) ?? 0) + 1;
  repeatedErrorCounts.set(signature, count);

  if (count > 5) {
    return false;
  }

  const safeProperties = {
    ...getBuildContext(),
    ...sanitizeTelemetryProperties(properties),
    errorName: normalizedError.name,
    errorMessage: normalizedError.message,
    repeatCount: count,
  };
  const client = getApplicationInsightsClient();

  if (shouldUseConsoleSink()) {
    console.error('[telemetry:error]', normalizedError, safeProperties);
  }

  if (client && typeof client.trackException === 'function') {
    client.trackException({ exception: normalizedError }, safeProperties);
    flushApplicationInsightsClient(client);
  }
  return true;
}

export function flushTelemetry() {
  flushApplicationInsightsClient(appInsightsClient);
}

export function resetTelemetryForTests() {
  appInsightsClient = null;
  appInsightsStarted = false;
  telemetryClientFactory = createApplicationInsightsClient;
  repeatedErrorCounts.clear();
}

export function setTelemetryClientFactoryForTests(factory) {
  appInsightsClient = null;
  appInsightsStarted = false;
  telemetryClientFactory = factory;
}

function normalizeError(error) {
  if (error instanceof Error) {
    return error;
  }

  return new Error(typeof error === 'string' ? error : 'Unknown error');
}

function flushApplicationInsightsClient(client) {
  if (client && typeof client.flush === 'function') {
    client.flush();
  }
}
